// Tab Template Selection Modal - Choose template when adding new tabs
import React, { useState, useEffect } from 'react';
import { X, BookOpen, GraduationCap, Briefcase, FlaskConical, User, Sparkles, Home, Laptop, Plane } from 'lucide-react';

export interface TabTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  accent: string;
  gradient: string;
  defaultLabel?: string;
}

const TEMPLATES: TabTemplate[] = [
  {
    id: 'default',
    name: 'Default Tab',
    description: 'Simple empty tab for any purpose',
    icon: Sparkles,
    accent: 'text-slate-200',
    gradient: 'from-slate-600/30 to-slate-500/30',
    defaultLabel: 'New Tab'
  },
  {
    id: 'journal',
    name: 'Journal',
    description: 'Daily logs and reflections',
    icon: BookOpen,
    accent: 'text-purple-200',
    gradient: 'from-purple-600/30 to-purple-500/30',
    defaultLabel: 'Journal'
  },
  {
    id: 'student',
    name: 'Student',
    description: 'Courses, assignments, and deadlines',
    icon: GraduationCap,
    accent: 'text-blue-200',
    gradient: 'from-blue-600/30 to-blue-500/30',
    defaultLabel: 'Student Tasks'
  },
  {
    id: 'professor',
    name: 'Professor',
    description: 'Lectures, grading, and office hours',
    icon: Briefcase,
    accent: 'text-amber-200',
    gradient: 'from-amber-600/30 to-amber-500/30',
    defaultLabel: 'Professor Tasks'
  },
  {
    id: 'research',
    name: 'Research',
    description: 'Projects, experiments, and publications',
    icon: FlaskConical,
    accent: 'text-cyan-200',
    gradient: 'from-cyan-600/30 to-cyan-500/30',
    defaultLabel: 'Research'
  },
  {
    id: 'personal',
    name: 'Personal',
    description: 'Personal goals and tasks',
    icon: User,
    accent: 'text-green-200',
    gradient: 'from-green-600/30 to-green-500/30',
    defaultLabel: 'Personal'
  },
];

interface TabTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: TabTemplate, customLabel: string) => void;
}

export default function TabTemplateModal({ isOpen, onClose, onSelect }: TabTemplateModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<TabTemplate | null>(null);
  const [customLabel, setCustomLabel] = useState('');
  const [step, setStep] = useState<'select' | 'customize'>('select');

  useEffect(() => {
    console.log('🎨 TabTemplateModal rendered, isOpen:', isOpen);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTemplateSelect = (template: TabTemplate) => {
    setSelectedTemplate(template);
    setCustomLabel(template.defaultLabel || '');
    setStep('customize');
  };

  const handleConfirm = () => {
    if (selectedTemplate && customLabel.trim()) {
      onSelect(selectedTemplate, customLabel.trim());
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setCustomLabel('');
    setStep('select');
    onClose();
  };

  const handleBack = () => {
    setStep('select');
    setSelectedTemplate(null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 backdrop-blur-xl border border-amber-500/20 rounded-2xl shadow-2xl shadow-amber-500/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-slate-900/95 to-slate-800/95 backdrop-blur-xl border-b border-amber-500/30 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-400">
              {step === 'select' ? '✨ Choose a Tab Template' : '✏️ Customize Tab'}
            </h2>
            <p className="text-sm text-amber-100/60 mt-1">
              {step === 'select' 
                ? 'Select a template to get started quickly' 
                : 'Give your new tab a custom name'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-amber-500/20 rounded-lg transition-colors group"
          >
            <X className="w-6 h-6 text-slate-400 group-hover:text-amber-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' ? (
            // Template Selection
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TEMPLATES.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`group relative p-6 rounded-xl border-2 transition-all text-left hover:scale-[1.02] bg-gradient-to-br ${template.gradient} border-amber-500/10 hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/10`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-black/30 ${template.accent} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-amber-50 mb-1 group-hover:text-amber-200 transition-colors">
                          {template.name}
                        </h3>
                        <p className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                          {template.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-600/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none" />
                  </button>
                );
              })}
            </div>
          ) : (
            // Customization Step
            <div className="space-y-6">
              {selectedTemplate && (
                <>
                  {/* Selected Template Preview */}
                  <div className={`p-6 rounded-xl border-2 bg-gradient-to-br ${selectedTemplate.gradient} border-amber-500/30 shadow-lg shadow-amber-500/5`}>
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl bg-black/30 ${selectedTemplate.accent}`}>
                        {React.createElement(selectedTemplate.icon, { className: 'w-6 h-6' })}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-amber-100">
                          {selectedTemplate.name} Template
                        </h3>
                        <p className="text-sm text-amber-100/60">
                          {selectedTemplate.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Label Input */}
                  <div>
                    <label className="block text-sm font-semibold text-amber-200 mb-2">
                      Tab Label *
                    </label>
                    <input
                      type="text"
                      value={customLabel}
                      onChange={(e) => setCustomLabel(e.target.value)}
                      placeholder="Enter tab name..."
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleConfirm();
                        if (e.key === 'Escape') handleBack();
                      }}
                      className="w-full bg-black/30 border-2 border-amber-500/30 rounded-lg px-4 py-3 text-amber-50 placeholder-amber-300/40 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all"
                    />
                    <p className="text-xs text-amber-100/50 mt-2">
                      💡 This will be the name displayed on your tab
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-amber-500/20">
                    <button
                      onClick={handleBack}
                      className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/50 rounded-lg transition-colors text-slate-200 font-medium"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={!customLabel.trim()}
                      className="px-6 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg transition-all text-white font-bold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50"
                    >
                      ✨ Create Tab
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
